import { Button, Card, Divider, Form, Select } from "antd"
import { FormikProvider, useFormik } from "formik"
import ErrorMessage from "components/base/error-message"
import { TApiResponse } from "@timenotes/shared/src/services/api-client/types"
import { TApiProjectDefaults } from "@timenotes/shared/src/services/api-client/settings-api"
import ClientSelectField from "components/timenotes/clients/client-select-field"

export interface IProjectSettingsFormObject {
  projectClientManager: 'admin' | 'member'
  projectDefaults: TApiProjectDefaults
}

export interface ProjectSettingsFormProps {
  initialValues: IProjectSettingsFormObject
  onSubmit(values: IProjectSettingsFormObject): Promise<TApiResponse>
}

const ProjectSettingsForm = (props: ProjectSettingsFormProps) => {
  const initialValues = props.initialValues
  const formik = useFormik<IProjectSettingsFormObject>({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: async (values, formik) => {
      const response = await props.onSubmit(values)

      if (!response.ok && response.errors) {
        formik.setErrors(response.errors)
      }

      if (response.ok) {
        formik.resetForm({ values })
      }
    }
  })

  return (
    <FormikProvider value={formik}>
      <Form
        onSubmitCapture={formik.handleSubmit}
        layout="vertical"
      >
        <Card
          style={{
            width: '400px'
          }}
        >
          <h2>Projects Settings</h2>

          <Divider style={{ marginTop: 0 }} />

          <Form.Item
            label="Who can create new projects?"
          >
            <Select
              value={formik.values.projectClientManager}
              onChange={(value) => {
                formik.setFieldValue('projectClientManager', value)
              }}
              allowClear={false}
            >
              <Select.Option key="admin" value="admin">Admin</Select.Option>
              <Select.Option key="member" value="member">Member</Select.Option>
            </Select>

            <ErrorMessage msg={formik.errors.projectClientManager} />
          </Form.Item>

        </Card>

        <Card
          style={{
            width: '400px'
          }}
        >
          <h2>New Project Defaults</h2>

          <Divider />

          <Form.Item
            label="Is new project public or private by default?"
          >
            <Select
              value={formik.values.projectDefaults.visibility ? 'public' : 'private'}
              onChange={(value) => {
                formik.setFieldValue('projectDefaults.visibility', value === 'public')
              }}
              allowClear={false}
            >
              <Select.Option key="public" value="public">Public</Select.Option>
              <Select.Option key="private" value="private">Private</Select.Option>
            </Select>

            <ErrorMessage msg={formik.errors.projectDefaults?.visibility} />
          </Form.Item>

          <Form.Item
            label="Default client for new projects"
          >
            <ClientSelectField
              name="projectDefaults.clientId"
            />
            <ErrorMessage msg={formik.errors.projectDefaults?.clientId} />
          </Form.Item>

          <Form.Item
            label="Default task management policy for new projects"
          >
            <Select
              value={formik.values.projectDefaults.taskCreator}
              onChange={(value) => {
                formik.setFieldValue('projectDefaults.taskCreator', value)
              }}
              style={{ width: '100%' }}
            >
              <Select.Option value="nobody"><b>Nobody</b> - no one can create or update tasks inside timenotes</Select.Option>
              <Select.Option value="member"><b>Member</b> - all members of the project</Select.Option>
              <Select.Option value="project_manager"><b>Project Manager</b> - assigned to the project</Select.Option>
              <Select.Option value="admin"><b>Admin</b> - of the workspace</Select.Option>
            </Select>
            <ErrorMessage msg={formik.errors.projectDefaults?.taskCreator} />
          </Form.Item>

          <Form.Item
            label="Is new project billable by default?"
          >
            <Select
              value={formik.values.projectDefaults.billableEnabled ? 'true' : 'false'}
              onChange={(value) => {
                formik.setFieldValue('projectDefaults.billableEnabled', value === 'true')
              }}
              style={{ width: '100%' }}
            >
              <Select.Option value="false">Non billable</Select.Option>
              <Select.Option value="true">Billable</Select.Option>
            </Select>
            <ErrorMessage msg={formik.errors.projectDefaults?.billableEnabled} />
          </Form.Item>

          {formik.values.projectDefaults.billableEnabled && (
            <Form.Item
              label="Are time logs for new projects billable by default?"
            >
              <Select
                value={formik.values.projectDefaults.defaultIsBillableTimeLogs ? 'true' : 'false'}
                onChange={(value) => {
                  formik.setFieldValue('projectDefaults.defaultIsBillableTimeLogs', value === 'true')
                }}
                style={{ width: '100%' }}
              >
                <Select.Option value="false">Non billable time entries by default</Select.Option>
                <Select.Option value="true">Billable time entries by default</Select.Option>
              </Select>
              <ErrorMessage msg={formik.errors.projectDefaults?.defaultIsBillableTimeLogs} />
            </Form.Item>
          )}

          <div style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <Button
              type={formik.dirty ? 'primary' : 'default'}
              htmlType="submit"
              disabled={!formik.dirty || formik.isSubmitting}
              loading={formik.isSubmitting}
              onClick={() => {
                formik.submitForm()
              }}
            >
              Update
            </Button>
          </div>
        </Card>
      </Form>
    </FormikProvider>
  )
}

export default ProjectSettingsForm 